import { Box, Container, Flex, Grid, IconButton } from '@radix-ui/themes'
import { useSpring } from '@react-spring/web'
import { Link, NavLink, useLocation, useNavigation } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxHamburgerMenu } from 'react-icons/rx'

import { AnimatedLogo } from '#app/components/common/logo'
import { Home, Message_1, Product_3, User_2 } from '#app/components/icon'
import { type Locale } from '#app/models/locale'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	ROOT_APP_VIEW,
	ROOT_I18NEXT,
} from '#app/routes/_layout/route-config.ts'
import { Search } from '#app/routes/_layout/search'
import { useCart } from '#app/utils/client/cart-context.tsx'

const menu = [
	{
		label: 'home',
		path: '',
		Icon: Home,
	},
	{
		label: 'products',
		path: '/products',
		Icon: Product_3,
	},
	{
		label: 'blog',
		path: '/blog',
		Icon: Message_1,
	},
	{
		label: 'profile',
		path: '/profile',
		Icon: User_2,
	},
]

/* -------------------------------------------------------------------------------------------------
 * Navbar
 * -----------------------------------------------------------------------------------------------*/

type NavbarProps = {
	locale: Locale
	loggedIn: boolean
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const Navbar: React.FC<NavbarProps> = ({ locale, loggedIn, setApp }) => {
	const { cartState } = useCart()
	const { pathname, search } = useLocation()
	const { t } = useTranslation(ROOT_I18NEXT.routeRoot)
	const { location } = useNavigation()
	const ref = React.useRef<HTMLDivElement | null>(null)
	const [, api] = useSpring(
		() => ({
			x: 0,
			config: {
				tension: 60,
				friction: 30,
				duration: 500,
			},
		}),
		[],
	)
	const [scroll, setScroll] = React.useState(false)
	const [isOut, setIsOut] = React.useState(true)

	const onScroll = () => {
		setScroll(window.scrollY > 20)
	}

	React.useEffect(() => {
		window.addEventListener('scroll', onScroll)

		return () => window.removeEventListener('scroll', onScroll)
	}, [])

	React.useEffect(() => setIsOut(true), [location])

	return (
		<>
			<Flex
				display={{ lg: 'none' }}
				top="0"
				position="sticky"
				pb="3"
				className="z-40 bg-gray-1"
			>
				<Container px="5">
					<Flex display={{ lg: 'none' }} direction="column" mt="3">
						<Flex justify="between" align="center">
							<Link to={`/${locale}`}>
								<AnimatedLogo />
							</Link>
							<Box
								display={{ initial: 'none', sm: 'block' }}
								mx="4"
								width="100%"
								maxWidth="28rem"
							>
								<Search
									cartCount={cartState.itemsCount}
									locale={locale}
									setApp={setApp}
								/>
							</Box>
							<Flex align="center" gapX="3">
								<Link
									to={`/${locale}/profile`}
									prefetch="viewport"
									onClick={(e) => {
										if (
											!loggedIn &&
											!pathname.includes('login') &&
											!pathname.includes('registration') &&
											!pathname.includes('reset-password')
										) {
											e.preventDefault()
											setApp((prev) => ({
												...prev,
												modalView: ROOT_APP_VIEW.loginView,
												displayModal: true,
												options: {
													...prev.options,
													redirect: null,
												},
											}))
										}
									}}
								>
									<IconButton variant="soft" size="4">
										<User_2 className="fill-transparent stroke-[var(--accent-a11)] stroke-[18]" />
									</IconButton>
								</Link>
								<Link
									to={`${pathname}${search}#footer`}
									prefetch="viewport"
									onClick={(e) => {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											sidebarView: ROOT_APP_VIEW.menuView,
											displaySidebar: true,
										}))
									}}
								>
									<IconButton variant="soft" size="4">
										<RxHamburgerMenu size="1.5em" />
									</IconButton>
								</Link>
							</Flex>
						</Flex>
						<Box display={{ sm: 'none' }} mt="4">
							<Search
								cartCount={cartState.itemsCount}
								locale={locale}
								setApp={setApp}
							/>
						</Box>
					</Flex>
				</Container>
			</Flex>

			<Box display={{ initial: 'none', lg: 'block' }} mb="4rem">
				<Box
					position="fixed"
					top="0"
					width="100%"
					py="4"
					className={cn(
						'z-40 rounded bg-gray-2',
						'transition-all duration-700 ease-in-out',
						{
							'!py-2 shadow-lg shadow-accent-3': scroll,
						},
					)}
				>
					<Container px="5">
						<Box asChild position="relative" p="2" height="4rem">
							<nav>
								<Flex
									justify="start"
									align="center"
									position="absolute"
									bottom="0"
									top="0"
									left="0"
								>
									<Link to={`/${locale}`}>
										<AnimatedLogo />
									</Link>
								</Flex>
								<Flex
									justify="center"
									align="center"
									position="absolute"
									top="0"
									bottom="0"
									left="50%"
								>
									<Grid
										asChild
										columns="4"
										gapX="4"
										position="relative"
										left="-50%"
									>
										<ul>
											{menu.map(({ label, path, Icon }) => (
												<li key={label}>
													<NavLink
														to={`/${locale}${path}`}
														prefetch="viewport"
														end={path === ''}
														onClick={(e) => {
															if (
																path === '/profile' &&
																!loggedIn &&
																!pathname.includes('login') &&
																!pathname.includes('registration') &&
																!pathname.includes('reset-password')
															) {
																e.preventDefault()
																setApp((prev) => ({
																	...prev,
																	modalView: ROOT_APP_VIEW.loginView,
																	displayModal: true,
																	options: {
																		...prev.options,
																		redirect: null,
																	},
																}))
															}
														}}
													>
														{({ isActive, isPending }) => (
															<Box
																onMouseOver={(e) => {
																	if (ref.current) {
																		Promise.all(
																			api.start({
																				x:
																					e.currentTarget.offsetLeft -
																					ref.current.offsetLeft,
																			}),
																		).catch(console.error)
																	}
																}}
																onClick={() => {
																	api.set({ x: 0 })
																}}
																ref={
																	(isActive && !location) ||
																	isPending ||
																	(isOut && path === '')
																		? ref
																		: undefined
																}
																className={cn('menu-item', {
																	active:
																		(isActive && !location) ||
																		isPending ||
																		(isOut && path === ''),
																})}
															>
																{/*((isActive && !location) || isPending || (isOut && path === '')) && (
                              <animated.div style={spring} className={cn('menu-item-top', {
                                'active': (isActive && !location) || isPending || (isOut && path === ''),
                              })}>
                                <div className={cn('light', {
                                  'active': (isActive && !location) || isPending,
                                })} />
                              </animated.div>
                            )*/}
																<Flex direction="column" align="center" pt="1">
																	<Icon
																		width={40}
																		height={40}
																		className={cn(
																			'text-xl',
																			(isActive && !location) || isPending
																				? 'fill-transparent stroke-accent-10 stroke-12'
																				: 'fill-transparent stroke-gray-10 stroke-12',
																		)}
																	/>
																	<small
																		className={cn(
																			'whitespace-nowrap text-center text-xs',
																			(isActive && !location) || isPending
																				? 'text-accent-7'
																				: 'text-gray-7',
																		)}
																	>
																		{t(`label.${label}`)}
																	</small>
																</Flex>
															</Box>
														)}
													</NavLink>
												</li>
											))}
										</ul>
									</Grid>
								</Flex>
								<Flex
									justify="start"
									align="center"
									position="absolute"
									bottom="0"
									top="0"
									right="0"
								>
									<Search
										cartCount={cartState.itemsCount}
										locale={locale}
										setApp={setApp}
									/>
								</Flex>
							</nav>
						</Box>
					</Container>
				</Box>
			</Box>
		</>
	)
}
Navbar.displayName = 'Navbar'

/* -----------------------------------------------------------------------------------------------*/

export { Navbar, menu }
